<template>
  <section ref="partialDiv">
    <div
      :class="{
        'text-center text-xl font-semibold leading-9 text-www-color-neutral-medium-dark md:text-2xl md:leading-[2.6875rem] lg:text-5xl lg:leading-[5.4rem]': true,
      }"
    >
      {{ title }}
    </div>
    <div
      :class="{
        'container mx-auto': true,
      }"
    >
      <div
        :class="{
          'my-6': true,
        }"
      >
        <component
          :is="getComponent(description?._type)"
          v-if="description"
          :value="description"
        />
        <component
          :is="getComponent(willBeRedirected._type)"
          v-if="willBeRedirected"
          :value="willBeRedirected"
        />
      </div>
      <div
        :class="{
          'flex justify-center gap-6': true,
        }"
      >
        <UssErrorCardComponent
          v-for="card in errorCards"
          :key="card._key"
          :data="card"
        />
      </div>
    </div>
    <template v-if="screenPartialData?.sectionBuilder?.length > 0">
      <component
        :is="getComponent(component._type)"
        v-for="component in screenPartialData?.sectionBuilder"
        :key="component._key"
        :value="component"
      />
    </template>
  </section>
</template>

<script lang="ts" setup>
import { FieldHtmlNameUtil } from '@onshape/www-inertia';

import UssErrorCardComponent from './UssErrorCard.vue';
import type {
  UssErrorCard,
  CodeEmbedPortableText,
  SignupPartialViewsSectionBuilder,
} from '~/sanity/types';
import { getComponent } from '~/components/Mapper';
import type UnifiedSignupSessionInfo from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupSessionInfo';
import type EmailCheck401SignupScreen from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/screens/EmailCheck401SignupScreen';
import UnifiedSignupStartScreen from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupStartScreen';
import RedirectUtil from '~/hekate-www/external/util/RedirectUtil';
import { setMaskedEmailInElements } from '~/hekate-www/external/util/EmailMaskUtil';

interface Props {
  signupScreen: EmailCheck401SignupScreen;
  sessionInfo: UnifiedSignupSessionInfo;
  title?: string;
  description?: CodeEmbedPortableText;
  willBeRedirected?: CodeEmbedPortableText;
  errorCards: ({ _key: string } & UssErrorCard)[];
  screenPartialData: SignupPartialViewsSectionBuilder;
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Sign up for Onshape',
  description: undefined,
  willBeRedirected: undefined,
});

const emit = defineEmits<{
  updatePage: [];
  setEduRenew: [];
  setProUpgrade: [];
}>();

const partialDiv = ref<HTMLElement | null>(null);

// Mask and display email in elements with hekate-data="email" attribute
const onCodeEmbedRendered = () => {
  const email = props.signupScreen.getField(FieldHtmlNameUtil.email).val as string;
  setMaskedEmailInElements(partialDiv.value, email);

  // Set up click listeners for intent and pro upgrade buttons
  const intentButton = partialDiv.value?.querySelector('[uss-session-info-intent]');
  if (intentButton) {
    intentButton.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        props.signupScreen.setEduRenew();
        emit('updatePage');
      },
      { once: true }
    );
  }

  // Pro trial button - show/hide based on disco eligibility
  const proTrialButton = partialDiv.value?.querySelector('[uss-session-info-start-screen]');
  if (proTrialButton) {
    if (props.sessionInfo.isDiscoEligible) {
      proTrialButton.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          props.signupScreen.setProUpgrade();
          emit('updatePage');
        },
        { once: true }
      );
    } else {
      proTrialButton.closest('.col-md-3')?.classList.add('d-none');
    }
  }

  // Handle buyStandard redirect
  if (props.sessionInfo.startScreen.compareTo(UnifiedSignupStartScreen.buyStandard)) {
    partialDiv.value?.querySelectorAll('.error-box').forEach((elm) => elm.classList.add('d-none'));
    // hide generic error msg, show redirect specific msg
    const toHide = partialDiv.value?.querySelector('[hekate-data=options-msg]');
    const toShow = partialDiv.value?.querySelector('[hekate-data=redirect-msg]');
    if (toHide && toShow) {
      toHide.classList.add('d-none');
      toShow.classList.remove('d-none');
    }

    RedirectUtil.redirectTo({
      delay: 3000,
      target: props.signupScreen.getField(FieldHtmlNameUtil.signupRedirectUrl).val as string,
    });
  }
};
onMounted(() => {
  partialDiv.value?.addEventListener('code-embed-rendered', onCodeEmbedRendered);
});
onUnmounted(() => {
  partialDiv.value?.removeEventListener('code-embed-rendered', onCodeEmbedRendered);
});
</script>

<style>
.d-none {
  display: none;
}
</style>
